import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import logoSrc from '@/assets/landing/logo_migro.png';

interface NavGroupItem {
  label: string;
  to: string;
  description?: string;
}

interface NavGroup {
  label: string;
  items: NavGroupItem[];
}

interface NavLinkItem {
  label: string;
  to: string;
}

const PLATAFORMA: NavGroup = {
  label: 'Plataforma',
  items: [
    { label: 'Pili — Asistente IA', to: '/abogados/pili', description: 'Borradores, revisión y consultas legales' },
    { label: 'Leads cualificados', to: '/abogados/leads', description: 'Cómo cualificamos y enviamos los casos' },
    { label: 'Casos de éxito', to: '/abogados/casos-exito', description: 'Testimonios y métricas reales' },
  ],
};

const PLANES: NavGroup = {
  label: 'Planes',
  items: [
    { label: 'Ver comparativa', to: '/abogados/planes', description: 'Compara los 3 planes lado a lado' },
    { label: 'Emprendedor', to: '/abogados/planes/emprendedor', description: 'Para abogados que arrancan' },
    { label: 'Abogado Independiente', to: '/abogados/planes/abogado-independiente', description: 'Para escalar facturación' },
    { label: 'Despacho', to: '/abogados/planes/despacho', description: 'Para despachos consolidados' },
  ],
};

const SIMPLE_LINKS: NavLinkItem[] = [
  { label: 'Cómo funciona', to: '/abogados/como-funciona' },
  { label: 'Recursos', to: '/abogados/recursos' },
  { label: 'Contacto', to: '/abogados/contacto' },
];

interface DropdownProps {
  group: NavGroup;
  onItemClick?: () => void;
}

function NavDropdown({ group, onItemClick }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const handleEnter = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleLeave = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-300 transition hover:text-white"
      >
        {group.label}
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 top-full z-50 mt-3 w-72 -translate-x-1/2 rounded-xl border border-white/10 bg-[#1A202C]/95 p-2 shadow-2xl backdrop-blur-md">
          <div className="space-y-1">
            {group.items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => {
                  setOpen(false);
                  onItemClick?.();
                }}
                className="block rounded-lg px-3 py-2.5 transition hover:bg-white/[0.06]"
              >
                <p className="text-sm font-medium text-white">{item.label}</p>
                {item.description && (
                  <p className="mt-0.5 text-xs text-slate-400">{item.description}</p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Cerrar menú móvil al cambiar de ruta
  useEffect(() => {
    setOpen(false);
    setExpandedGroup(null);
  }, [location.pathname]);

  const closeMobile = () => {
    setOpen(false);
    setExpandedGroup(null);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${
          scrolled || open
            ? 'bg-[#1A202C]/95 shadow-lg backdrop-blur-md'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/abogados" aria-label="Migro — Inicio">
            <img src={logoSrc} alt="Migro" className="h-8 brightness-0 invert" />
          </Link>

          {/* Desktop links */}
          <div className="hidden items-center gap-7 md:flex">
            <NavDropdown group={PLATAFORMA} />
            <NavDropdown group={PLANES} />
            {SIMPLE_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `text-sm font-medium transition ${
                    isActive ? 'text-white' : 'text-slate-300 hover:text-white'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/abogados/planes"
              className="rounded-lg bg-orange-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-500/25 transition hover:bg-orange-600 hover:scale-[1.02] active:scale-[0.98]"
            >
              Empezar ahora
            </Link>
          </div>

          {/* Hamburger */}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white transition hover:bg-white/10 md:hidden"
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={open}
          >
            {open ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile fullscreen menu */}
      <div
        className={`fixed inset-0 top-[72px] z-50 flex flex-col gap-2 overflow-y-auto bg-[#1A202C] px-4 pb-10 pt-6 transition-all duration-300 md:hidden ${
          open ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        {[PLATAFORMA, PLANES].map((group) => {
          const isExpanded = expandedGroup === group.label;
          return (
            <div key={group.label} className="border-b border-white/10">
              <button
                type="button"
                onClick={() => setExpandedGroup(isExpanded ? null : group.label)}
                aria-expanded={isExpanded}
                className="flex w-full items-center justify-between py-4 text-left text-base font-medium text-white"
              >
                {group.label}
                <ChevronDownIcon
                  className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {isExpanded && (
                <div className="space-y-1 pb-3 pl-3">
                  {group.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={closeMobile}
                      className="block rounded-lg px-3 py-2 text-sm text-slate-300 transition hover:bg-white/[0.06] hover:text-white"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {SIMPLE_LINKS.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            onClick={closeMobile}
            className="border-b border-white/10 py-4 text-base font-medium text-white transition hover:text-emerald-400"
          >
            {link.label}
          </Link>
        ))}

        <Link
          to="/abogados/planes"
          onClick={closeMobile}
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/25 transition hover:bg-orange-600"
        >
          Empezar ahora
        </Link>
      </div>
    </>
  );
}
